import { type Node } from "@xyflow/react";

export const createNodeByType = (
  type: string,
  position: { x: number; y: number },
  existingNodes: Node<any>[] = []
): Node<any> => {
  const nodeTypeMap: Record<string, Partial<Node<any>>> = {
    // Triggers
    "document-upload": {
      type: "FILE-UPLOAD",
      data: {
        name: "FileUploadNode",
        file: null,
      },
    },
    "document-store": {
      type: "DOCUMENT-STORE",
      data: {
        name: "DocumentStoreNode",
        document_store_id: "",
      },
    },

    // Actions
    "ocr-processing": {
      type: "FILE-OCR",
      data: {
        name: "OCRProcessingNode",
        file_processing_type: "",
        key: "",
      },
    },
    parsing: {
      type: "FILE-PARSE",
      data: {
        name: "DocumentParsingNode",
        summary: "",
        schema_id: "",
      },
    },
    "email-parsing": {
      type: "EMAIL-OCR",
      data: {
        name: "EmailParsingNode",
        file_processing_type: "AI_OCR",
        llm_model_name: "gemini-2.5-flash-lite",
        prompt: "",
        file_context: "",
      },
    },
    "cloud-vision": {
      type: "CLOUD-VISION",
      data: {
        name: "CloudNode",
        api_key: "",
        files: ""
      }
    },
    "gemini-understanding": {
      type: "GEMINI-UNDERSTANDING",
      data: {
        name: "GeminiUnderstandingNode",
        files: "",
        prompt: "",
        model_name: "",
        additional_content: "",
        schema: {},
      }
    },
    "EXCEL": {
      type: "EXCEL",
      data: {
        name: "ExportToExcel",
        data: "",
        merge_data: "",
        file_name: "Report.xlsx",
        header_mapping: {
          "file_info.name": "file_name",
        },
      },
    },
    "WORD": {
      type: "WORD",
      data: {
        name: "WordNode",
        file_name: "Project_Status_Report.docx",
        prompt: "",
        content: "",
        summary: "",
      },
    },
    "CODE": {
      type: "CODE",
      data: {
        name: "CodeNode",
        code: "// Add two numbers\nconst a = inputs.arg1 || 0;\nconst b = inputs.arg2 || 0;\n\nreturn a + b;",
        language: "javascript",
        inputs: {}
      },
    },

    "llm-helper": {
      type: "LLM",
      data: {
        name: "LLMProcessingNode",
        model_name: "",
        api_key: "",
      },
    },
    summarize: {
      type: "SUMMARY",
      data: {
        name: "SummarizeNode",
        prompt: "",
      },
    },
    "SMART-PARSE": {
      type: "SMART-PARSE",
      data: {
        name: "SmartExtractionNode",
        inputs: {
          document_store_id: "",
          file_id: [],
          schema: {},
          summary: "",
          content: "",
        },
      },
    },
    "compare-data": {
      type: "FILE-COMPARE",
      data: {
        name: "DocumentCompareNode",
        source_file_id: "",
        target_file_id: "",
      },
    },

    // Output
    mongodb: {
      type: "FILE-DATABASE",
      data: {
        name: "MongoDBNode",
        connection_string: "",
        database_name: "",
        collection_name: "",
        result: "",
      },
    },
    transform: {
      type: "TRANSFORM",
      data: {
        name: "TransformNode",
        mappings: [],
      },
    },
    webhook: {
      type: "WEBHOOK",
      data: {
        name: "WebhookNode",
        webhook_url: "",
        method: "POST",
        webhook_key: "",
        webhook_value: "",
        api_key_format: "headers",
        results: "",
      },
    },
    "http-request": {
      type: "HTTP",
      data: {
        name: "HttpNode",
        url_name: "HTTP Request",
        method: "POST",
      },
    },
    "http-api": {
      type: "HTTP-API",
      data: {
        name: "HttpAPINode",
        url: "",
        method: "GET",
      },
    },
    "one-drive": {
      type: "ONEDRIVE-CONNECTOR",
      data: {
        name: "ConnectorNode",
        access_token: "",
        trigger_type: "",
        folder_id: "",
        poll_interval: 5, // optional default for time-based triggers
      },
    },
    GMAIL_CONNECTOR: {
      type: "GMAIL_CONNECTOR",
      data: {
        name: "GmailTriggerNode",
        gmail_credential_id: "",
        template_id: "",
      },
    },
    "OUTLOOK-CONNECTOR": {
      type: "OUTLOOK-CONNECTOR",
      data: {
        name: "OutlookConnectorNode",
        outlook_credential_id: "",
        template_id: "",
      },
    },
    schedule: {
      type: "SCHEDULE",
      data: {
        name: "ScheduleNode",
        // n8n-style structured interval — auto-derives cron_expression on the UI side
        schedule_kind:  "interval",
        interval_unit:  "minutes",
        interval_value: 5,
        at_time:        "09:00",
        day_of_week:    1,
        day_of_month:   1,
        cron_expression: "*/5 * * * *",
        timezone: "Asia/Kolkata",
      },
    },
    "pdf-generator": {
      type: "PDF",
      data: {
        name: "PDFGeneratorNode",
        pdfInputData: "",
      },
    },
    "onedrive-upload": {
      type: "ONEDRIVE-UPLOAD",
      data: {
        name: "OneDriveUploadNode",
        inputs: {
          access_token: "",
          folder_id: "",
          filename: "",
          buffer: "",
        },
      },
    },
    "for-each": {
      type: "FOR-EACH",
      data: {
        name: "ForEachNode",
        arrayInput: "",
        itemVariableName: "item",
      },
    },
    "error-catcher": {
      type: "ERROR-CATCHER",
      data: {
        name: "ErrorCatcher",
      },
    },
    "if-else": {
      type: "IF-ELSE",
      data: {
        name: "IfElseNode",
        condition: "",
        input_variables: "",
      },
    },
    switch: {
      type: "SWITCH",
      data: {
        name: "SwitchNode",
        rules: [],
      },
    },
    validator: {
      type: "VALIDATOR",
      data: {
        name: "ValidatorNode",
        input_json: "",
        instruction: "",
        iteration_limit: 3,
      },
    },
    "OUTLOOK-SEND": {
      type: "OUTLOOK-SEND",
      data: {
        name: "OutlookSendNode",
        outlook_credential_id: "",
        to: [],
        subject: "",
        body: "",
      },
    },
    "GMAIL-SEND": {
      type: "GMAIL-SEND",
      data: {
        name: "GmailSendNode",
        gmail_credential_id: "",
        to: [],
        subject: "",
        body: "",
      },
    },
    "web-scraper": {
      type: "WEB-SCRAPER",
      data: {
        name: "WebScraperNode",
        seed_url: "",
        allowed_file_types: [],
        use_playwright_fallback: false,
        respect_robots_txt: true,
        crawl_subdomains: false,
        enable_change_detection: false,
        poll_timeout_ms: 300000,
        poll_interval_ms: 5000,
        custom_headers: {},
      },
    },
    "docxiq-export": {
      type: "DOCXIQ-EXPORT",
      data: {
        name: "DocxiqExportNode",
        document_store_id: "",
        file_object: "",
        file_url: "",
        text_content: "",
        file_url_file_name: "",
        text_content_file_name: "",
        folder_id: "",
        mime_type: "",
        ai_performance_level: "MEDIUM",
        file_processing_type: "AI_OCR",
      },
    },
    "postgres": {
      type: "POSTGRES",
      data: {
        name: "PostgreSQLNode",
        connection: { host: "", port: 5432, database: "", user: "", password: "", ssl: false },
        operation: "SELECT",
        schema: "public",
        table: "",
        order_direction: "ASC",
      },
    },
  };

  const nodeTemplate = nodeTypeMap[type];
  if (!nodeTemplate) {
    throw new Error(
      `Unknown node type: ${type}. Available types: ${Object.keys(
        nodeTypeMap
      ).join(", ")}`
    );
  }

  console.log(existingNodes);

  const baseName = nodeTemplate.data?.name || "Node";

  const existingCount = existingNodes.filter((node) => {
    const nodeName = node.data?.name || "";
    return nodeName.startsWith(baseName);
  }).length;

  const dynamicName = `${baseName}${existingCount + 1}`;

  const newNodeId = `${type}-${Math.random().toString(36).substring(2, 9)}`;
  return {
    id: newNodeId,
    position,
    ...nodeTemplate,
    data: {
      ...nodeTemplate.data,
      name: dynamicName,
    },
  } as Node<any>;
};
